import { motion } from 'motion/react';
import { PlayIcon, FolderIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { shouldUseComplexAnimations } from '../utils/performance';
import { useEffect, useState } from 'react';

export default function Hero() {
  const [enableAnimations, setEnableAnimations] = useState(true);

  useEffect(() => {
    // Détecte si les animations complexes doivent être désactivées
    setEnableAnimations(shouldUseComplexAnimations());
  }, []);

  return (
    <section className="relative pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 hero-gradient"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-primary-900/40 text-primary-300 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              <PlayIcon className="w-4 h-4" />
              MP3 Random Selector
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-primary-300">Vos morceaux aléatoires,</span>
              <br />
              <span className="text-white">prêts à copier en quelques secondes</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Sélectionnez aléatoirement des MP3 depuis vos dossiers, filtrez par année ou durée, et copiez directement vers votre clé USB ou lecteur MP3 — sans aucun tri manuel.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
          >
            <a href="#download" className="btn-primary text-lg flex items-center justify-center">
              <PlayIcon className="w-6 h-6 mr-2" />
              Télécharger gratuitement
            </a>
            <a href="#features" className="btn-secondary text-lg flex items-center justify-center">
              <AdjustmentsHorizontalIcon className="w-6 h-6 mr-2" />
              Découvrir les fonctionnalités
            </a>
          </motion.div>
          
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto"
          >
            <div className="card text-center">
              <FolderIcon className="w-12 h-12 text-primary-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Dossiers locaux et réseau
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Ajoutez autant de dossiers que vous voulez et scannez-les tous d'un seul clic
              </p>
            </div>
            
            <div className="card text-center">
              <AdjustmentsHorizontalIcon className="w-12 h-12 text-accent-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Critères précis
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Année, durée, nombre de morceaux — une sélection vraiment personnalisée, sans tri manuel
              </p>
            </div>
            
            <div className="card text-center">
              <PlayIcon className="w-12 h-12 text-purple-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Version gratuite incluse
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Utilisez l'application gratuitement — jusqu'à 3 dossiers et 15 morceaux par sélection
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Éléments décoratifs flottants - Optimisés pour performance */}
      {enableAnimations && (
        <>
          <motion.div
            animate={{ 
              rotate: 360,
            }}
            transition={{ 
              duration: 30, 
              repeat: Infinity,
              ease: "linear",
              repeatType: "loop"
            }}
            className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-full blur-xl"
            style={{ willChange: 'transform' }}
          />
          <motion.div
            animate={{ 
              rotate: -360,
            }}
            transition={{ 
              duration: 40, 
              repeat: Infinity,
              ease: "linear",
              repeatType: "loop"
            }}
            className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-accent-500/20 to-purple-500/20 rounded-full blur-xl"
            style={{ willChange: 'transform' }}
          />
        </>
      )}
    </section>
  );
}
